import React, { useEffect } from 'react'
import { Table, TableHeader, TableBody, TableCell, TableRow, TableHead } from '@/components/ui/table'
import { tableHeadClass, tableCellClass } from '@/components/ui/table-styles'
import { AutoCompletePortal } from '@/components/ui/autocomplete-portal'
import { Button } from '@/components/ui/button'
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, X, MoreVertical, Calendar as CalendarIcon } from 'lucide-react'
import { Input } from '@/components/ui/input'
import type { Option } from '@/components/ui/autocomplete'
import PaginationButton from '@/components/ui/pagination-button'
import { useDebounce } from '@uidotdev/usehooks'
import { useQuery } from '@connectrpc/connect-query'
import { searchInventory } from '@/gen/pharmacy-PharmacyService_connectquery'
import { toast } from '../ui/use-toast'
import { ItemType, DiscountType } from '@/gen/inventory/common_pb'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { format } from 'date-fns'
import { pharmacyClient } from '@/connect'
import BarcodeModal from '@/routes/(pharma)/_layout/inventory/add-stock/-components/BarcodeModal'
import getPharmacyID from '@/utils/pharmacy_code'
import dayjs from 'dayjs'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'

type Row = {
  product: Option | undefined;
  productInput: string;
  batchNo: string;
  quantity: string;
  bonus: string;
  cost: string;
  price: string;
  expiryDate?: Date;
  productionDate?: Date;
  customBarcode: string;
  discountType: string;
  discountValue: string;
  note: string;
  type?: 'MEDICAL' | 'NON_MEDICAL';
  masterId?: string;
};

type CreatePurchaseInvoiceComponentProps = {
  rows: Row[];
  handleProductChange: (index: number, value: Option) => void;
  handleRowChange: (index: number, field: string, value: any) => void;
  handleClearRow: (index: number) => void;
  rowsPerPage?: number;
  bordersTrue?: boolean;
  handleSubmit: () => void;
  loading: boolean;
};

export const CreatePurchaseInvoiceComponent: React.FC<CreatePurchaseInvoiceComponentProps> = ({
  rows,
  handleProductChange,
  handleRowChange,
  handleClearRow,
  rowsPerPage = 10,
  bordersTrue = false,
  handleSubmit,
  loading
}) => {
  const [currentPage, setCurrentPage] = React.useState(1);
  const totalPages = Math.ceil(rows.length / rowsPerPage);
  const paginatedRows = rows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(currentPage + 1, totalPages - 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className={`flex flex-col h-full ${bordersTrue ? 'border rounded-lg' : ''}`}>
      <div className="flex-1 min-h-0 overflow-auto flex flex-col">
        <Table className="text-sm">
          <TableHeader>
            <TableRow className="text-sm">
              <TableHead className={tableHeadClass}>SL NO.</TableHead>
              <TableHead className={tableHeadClass}>Product Name</TableHead>
              <TableHead className={tableHeadClass}>Batch Number</TableHead>
              <TableHead className={tableHeadClass}>Quantity</TableHead>
              <TableHead className={tableHeadClass}>Bonus</TableHead>
              <TableHead className={tableHeadClass}>Cost (Excl Tax)</TableHead>
              <TableHead className={tableHeadClass}>Price (Excl Tax)</TableHead>
              <TableHead className={tableHeadClass}>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedRows.map((row, idx) => {
              const realIdx = (currentPage - 1) * rowsPerPage + idx;
              return (
                <IndividualRow
                  key={realIdx}
                  row={row}
                  realIdx={realIdx}
                  handleRowChange={handleRowChange}
                  handleClearRow={handleClearRow}
                  totalLength={rows.length}
                  handleProductChange={handleProductChange}
                />
              );
            })}
          </TableBody>
        </Table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-2 text-sm border-b bg-white">
            <p>{rows.length} <span className="text-gray-500 font-light">items</span></p>
            <div className="flex items-center gap-1">
              <PaginationButton onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                <ChevronsLeft className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                <ChevronLeft className="h-4 w-4" />
              </PaginationButton>
              {getPageNumbers().map((page, idx) => (
                page === '...'
                  ? <span key={`ellipsis-${idx}`} className="mx-1">...</span>
                  : <PaginationButton
                      key={page}
                      active={currentPage === page}
                      onClick={() => setCurrentPage(page as number)}
                      disabled={currentPage === page}
                    >
                      <span className="text-sm">{page}</span>
                    </PaginationButton>
              ))}
              <PaginationButton onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                <ChevronRight className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                <ChevronsRight className="h-4 w-4" />
              </PaginationButton>
            </div>
          </div>
        )}
      </div>
      <div className="w-full border-t px-4 py-3 bg-white flex items-center justify-end gap-2">
        <Button variant="outline">Save Draft</Button>
        <Button variant="default" className='bg-blue-700' onClick={handleSubmit} disabled={loading}>
          {loading ? 'Creating...' : 'Create Purchase Invoice'}
        </Button>
      </div>
    </div>
  );
};

const IndividualRow = ({
  row,
  realIdx,
  handleRowChange,
  handleClearRow,
  totalLength,
  handleProductChange
}: {
  row: Row;
  realIdx: number;
  handleRowChange: (index: number, field: string, value: any) => void;
  handleClearRow: (index: number) => void;
  totalLength: number;
  handleProductChange: (index: number, value: Option) => void;
}) => {
  const [selectedOption, setSelectedOption] = React.useState<Option | undefined>(row.product);
  const [searchInput, setSearchInput] = React.useState('');
  const [showDetailsModal, setShowDetailsModal] = React.useState(false);
  const [options, setOptions] = React.useState<Option[]>([]);
  const [barcodeModalOpen, setBarcodeModalOpen] = React.useState(false);
  const [generatedBarcodeData, setGeneratedBarcodeData] = React.useState<{
    barcode: string;
    productName: string;
    batchNumber: string;
    expiryDate: any;
    unit: string;
    unitType: string;
  } | null>(null);
  const [customBarcodeGenerating, setCustomBarcodeGenerating] = React.useState(false);

  const debouncedSearch = useDebounce(searchInput, 500);

  const { data, error, isLoading } = useQuery(searchInventory, {
    query: debouncedSearch,
    limit: 5,
    onlyAvailable: false
  });

  useEffect(() => {
    if (data) {
      const formattedOptions = data.items.map(item => ({
        value: item.id,
        label: item.tradeName + (item.scientificName ? ` - ${item.scientificName}` : '') + ` (${item.unit} ${item.unitType})`,
        type: item.type === ItemType.MEDICAL ? 'MEDICAL' : 'NON_MEDICAL',
        medical_master_id: item.type === ItemType.MEDICAL ? item.id : "",
        non_medical_master_id: item.type === ItemType.NON_MEDICAL ? item.id : "",
      }));
      setOptions(formattedOptions);
    }
  }, [data]);

  useEffect(() => {
    if (row.product) {
      setSelectedOption(row.product);
      setSearchInput(row.product.label || '');
    } else {
      setSelectedOption(undefined);
      setSearchInput('');
    }
  }, [row.product]);

  if (error) {
    toast({
      title: 'Error',
      description: 'Failed to fetch products. Please try again later.',
      variant: 'destructive',
      duration: 5000,
    });
  }

  return (
    <TableRow className="font-medium" key={realIdx}>
      <TableCell className={tableCellClass}>{realIdx + 1}</TableCell>
      <TableCell className={tableCellClass}>
        <AutoCompletePortal
          options={options}
          value={selectedOption}
          onValueChange={(value) => {
            setSelectedOption(value);
            handleProductChange(realIdx, value);
          }}
          input={searchInput}
          onInputChanged={setSearchInput}
          emptyMessage="No products found."
          placeholder="Select product"
          isLoading={isLoading}
          disabled={false}
          className=""
        />
      </TableCell>
      <TableCell className={tableCellClass}>
        <Input
          type="text"
          className="border rounded px-2 py-1 h-7 w-28"
          value={row.batchNo}
          onChange={e => handleRowChange(realIdx, 'batchNo', e.target.value)}
          disabled={!row.product}
          placeholder="Batch #"
        />
      </TableCell>
      <TableCell className={tableCellClass}>
        <Input
          type="number"
          className="border rounded px-2 py-1 h-7 w-20"
          value={row.quantity}
          onChange={e => handleRowChange(realIdx, 'quantity', e.target.value)}
          disabled={!row.product}
        />
      </TableCell>
      <TableCell className={tableCellClass}>
        <Input
          type="number"
          className="border rounded px-2 py-1 h-7 w-20"
          value={row.bonus}
          onChange={e => handleRowChange(realIdx, 'bonus', e.target.value)}
          disabled={!row.product}
        />
      </TableCell>
      <TableCell className={tableCellClass}>
        <Input
          type="number"
          className="border rounded px-2 py-1 h-7 w-24"
          value={row.cost}
          onChange={e => handleRowChange(realIdx, 'cost', e.target.value)}
          disabled={!row.product}
        />
      </TableCell>
      <TableCell className={tableCellClass}>
        <Input
          type="number"
          className="border rounded px-2 py-1 h-7 w-24"
          value={row.price}
          onChange={e => handleRowChange(realIdx, 'price', e.target.value)}
          disabled={!row.product || row.type === 'MEDICAL'}
        />
      </TableCell>
      <TableCell className={tableCellClass}>
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="xs"
            onClick={() => setShowDetailsModal(true)}
            disabled={!row.product}
          >
            <MoreVertical className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="xs"
            onClick={() => handleClearRow(realIdx)}
            className="text-gray-600 hover:text-red-500 hover:bg-red-100"
            disabled={totalLength <= 1 && !row.product}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Details Modal */}
        {showDetailsModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
            <div className="bg-white rounded-lg shadow-lg p-6 w-[500px] max-h-[90vh] overflow-y-auto">
              <h3 className="font-semibold mb-4 text-lg">Additional Item Details</h3>
              
              <div className="space-y-4">
                {/* Expiry Date */}
                <div>
                  <label className="block text-sm font-medium mb-2">Expiry Date</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !row.expiryDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {row.expiryDate ? format(row.expiryDate, "PPP") : "Select expiry date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar 
                        mode="single" 
                        selected={row.expiryDate} 
                        onSelect={(date) => handleRowChange(realIdx, 'expiryDate', date)}
                        fromDate={new Date()}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Production Date */}
                <div>
                  <label className="block text-sm font-medium mb-2">Production Date</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !row.productionDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {row.productionDate ? format(row.productionDate, "PPP") : "Select production date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar 
                        mode="single" 
                        selected={row.productionDate} 
                        onSelect={(date) => handleRowChange(realIdx, 'productionDate', date)}
                        toDate={new Date()}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Custom Barcode */}
                <div>
                  <label className="block text-sm font-medium mb-2">Custom Barcode</label>
                  <div className="flex gap-2">
                    <Input
                      value={row.customBarcode}
                      onChange={(e) => handleRowChange(realIdx, 'customBarcode', e.target.value)}
                      placeholder="Custom Barcode"
                    />
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={async () => {
                            // Validation: Check batch number
                            if (!row.batchNo || row.batchNo.trim() === '') {
                              toast({
                                title: "Batch Number Required",
                                description: "Please enter a batch number in the main table before generating barcode",
                                variant: "destructive",
                                duration: 3000,
                              });
                              return;
                            }

                            // Validation: Check expiry date
                            if (!row.expiryDate) {
                              toast({
                                title: "Expiry Date Required",
                                description: "Please select an expiry date before generating barcode",
                                variant: "destructive",
                                duration: 3000,
                              });
                              return;
                            }

                            // Validation: Check product
                            if (!row.masterId || !row.product) {
                              toast({
                                title: "Error",
                                description: "Product information not available",
                                variant: "destructive",
                                duration: 3000,
                              });
                              return;
                            }

                            try {
                              setCustomBarcodeGenerating(true);
                              
                              // Generate barcode
                              const response = await pharmacyClient.generateCustomBarcode({
                                masterId: row.masterId,
                                itemType: row.type === 'MEDICAL' ? ItemType.MEDICAL : ItemType.NON_MEDICAL,
                              });
                              
                              // Update form with generated barcode
                              handleRowChange(realIdx, 'customBarcode', response.barcode);
                              
                              // Prepare data for modal using form data
                              setGeneratedBarcodeData({
                                barcode: response.barcode as string,
                                productName: row.product.label || '',
                                batchNumber: row.batchNo,
                                expiryDate: row.expiryDate,
                                unit: '', // Can be extracted from product metadata if available
                                unitType: '', // Can be extracted from product metadata if available
                              });
                              
                              // Close details modal and open barcode modal
                              setShowDetailsModal(false);
                              setBarcodeModalOpen(true);
                              
                            } catch (error) {
                              console.error('Error generating barcode:', error);
                              toast({
                                title: "Error",
                                description: "Failed to generate barcode",
                                variant: "destructive",
                                duration: 3000,
                              });
                            } finally {
                              setCustomBarcodeGenerating(false);
                            }
                          }}
                          disabled={customBarcodeGenerating}
                        >
                          {customBarcodeGenerating ? "Generating..." : "Generate & Print"}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Batch number and expiry date required</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  </div>
                </div>

                {/* Discount Type */}
                <div>
                  <label className="block text-sm font-medium mb-2">Discount Type</label>
                  <Select
                    value={row.discountType}
                    onValueChange={(value) => handleRowChange(realIdx, 'discountType', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Discount Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={DiscountType.NONE.toString()}>None</SelectItem>
                      <SelectItem value={DiscountType.PERCENTAGE.toString()}>Percentage</SelectItem>
                      <SelectItem value={DiscountType.FIXED.toString()}>Fixed</SelectItem>
                      <SelectItem value={DiscountType.ALWAYS_FIXED.toString()}>Always Fixed</SelectItem>
                      <SelectItem value={DiscountType.ALWAYS_PERCENTAGE.toString()}>Always Percentage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Discount Value */}
                <div>
                  <label className="block text-sm font-medium mb-2">Discount Value</label>
                  <Input
                    type="number"
                    value={row.discountValue}
                    onChange={(e) => handleRowChange(realIdx, 'discountValue', e.target.value)}
                    placeholder="Discount Value"
                    disabled={row.discountType === DiscountType.NONE.toString()}
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium mb-2">Notes</label>
                  <textarea
                    className="w-full border rounded p-2 text-sm"
                    rows={3}
                    value={row.note}
                    onChange={(e) => handleRowChange(realIdx, 'note', e.target.value)}
                    placeholder="Add notes for this item..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </TableCell>
      {/* Barcode Modal */}
      {generatedBarcodeData && (
          <BarcodeModal
            open={barcodeModalOpen}
            onOpenChange={setBarcodeModalOpen}
            initialData={{
              pharmacyName: getPharmacyID(),
              productName: generatedBarcodeData.productName,
              expiryDate: generatedBarcodeData.expiryDate 
                ? dayjs(generatedBarcodeData.expiryDate).format("YYYY-MM-DD")
                : new Date().toISOString().split('T')[0],
              barcodeNumber: generatedBarcodeData.barcode,
            }}
            onPrintComplete={(barcodeNumber) => {
              toast({
                title: "Success",
                description: "Barcode printed successfully!",
                duration: 3000,
              });
              setBarcodeModalOpen(false);
              setGeneratedBarcodeData(null);
            }}
          />
        )}
      </TableRow>
  );
};

export default CreatePurchaseInvoiceComponent;